/**
 * GuestInviteButton — attorney mints a read-only guest-review link for a case (§6).
 *
 * A quiet, monochrome trigger in the attorney case-header action cluster that opens a
 * two-step modal (form -> success with copy). Calls the firm-scoped, deploy-verified
 * POST /api/v1/attorney/case/{caseId}/guest-invite. Orange appears ONLY on the two primary
 * actions (Generate link / Copy link) per the K3 design gate; everything else is monochrome.
 *
 * Coded by kimi 2.6 to a conductor-verified spec + K3 design brief; conductor-reviewed
 * (api.post auto-attaches the Clerk bearer here — no Authorization header; reset-on-close).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Link2, UserPlus, X } from 'lucide-react';
import { api } from '../../utils/api';

interface GuestInviteResponse {
  guest_url: string;
  token: string;
  report_id: string;
  guest_email: string;
  expires_at: string;
  read_only: boolean;
}

export default function GuestInviteButton({ caseId }: { caseId: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [expiresDays, setExpiresDays] = useState(7);
  const [emailError, setEmailError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [result, setResult] = useState<GuestInviteResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const urlRef = useRef<HTMLSpanElement>(null);

  const emailRegex = /.+@.+\..+/;
  const isEmailValid = emailRegex.test(email);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setEmail('');
    setName('');
    setExpiresDays(7);
    setEmailError(false);
    setBusy(false);
    setPostError(null);
    setResult(null);
    setCopied(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  const handleEmailBlur = () => {
    setEmailError(!emailRegex.test(email));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmailValid) {
      setEmailError(true);
      return;
    }
    setBusy(true);
    setPostError(null);
    try {
      const data = await api.post<GuestInviteResponse>(
        `/api/v1/attorney/case/${caseId}/guest-invite`,
        {
          guest_email: email.trim(),
          guest_name: name.trim() || undefined,
          expires_days: expiresDays,
        }
      );
      setResult(data);
    } catch (err: any) {
      setPostError(err?.message || 'TRY AGAIN');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.guest_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      if (urlRef.current) {
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(urlRef.current);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2 rounded px-3 transition-colors hover:border-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        style={{
          height: '32px',
          border: '1px solid var(--color-border)',
          background: 'transparent',
          fontFamily: "'Outfit', sans-serif",
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
        }}
        data-testid="invite-guest-button"
      >
        <UserPlus style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)' }} />
        Invite guest
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={(e) => {
            if (e.currentTarget === e.target) handleClose();
          }}
        >
          <div
            className="relative w-full rounded p-5"
            style={{
              maxWidth: '400px',
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute"
              style={{ top: '16px', right: '16px', color: 'var(--color-text-muted)' }}
              aria-label="Close"
            >
              <X style={{ width: '16px', height: '16px' }} />
            </button>

            {!result ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div
                    className="font-mono text-[10px] uppercase tracking-wide"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    GUEST ACCESS
                  </div>
                  <div
                    className="mt-1"
                    style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: '18px',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Invite guest reviewer
                  </div>
                  <p
                    className="mt-1"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '12px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    Read-only access to this case only. Nothing can be changed or uploaded.
                  </p>
                </div>

                <div>
                  <label
                    className="block font-mono text-[10px] uppercase tracking-wide mb-1.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Guest email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError && emailRegex.test(e.target.value)) {
                        setEmailError(false);
                      }
                    }}
                    onBlur={handleEmailBlur}
                    className="w-full rounded px-3 outline-none focus:outline-none focus:!border-[var(--color-text-muted)]"
                    style={{
                      height: '36px',
                      background: 'var(--color-bg-elevated)',
                      border: `1px solid ${emailError ? 'var(--color-error, #C24A3A)' : 'var(--color-border)'}`,
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '13px',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                  {emailError && (
                    <div
                      className="font-mono text-[11px] mt-1.5"
                      style={{ color: 'var(--color-error, #C24A3A)' }}
                    >
                      A VALID EMAIL IS REQUIRED
                    </div>
                  )}
                </div>

                <div>
                  <label
                    className="block font-mono text-[10px] uppercase tracking-wide mb-1.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Guest name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="For your reference"
                    className="w-full rounded px-3 outline-none focus:outline-none focus:!border-[var(--color-text-muted)]"
                    style={{
                      height: '36px',
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '13px',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                </div>

                <div>
                  <label
                    className="block font-mono text-[10px] uppercase tracking-wide mb-1.5"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Expires in
                  </label>
                  <select
                    value={expiresDays}
                    onChange={(e) => setExpiresDays(Number(e.target.value))}
                    className="w-full rounded px-3 outline-none focus:outline-none focus:!border-[var(--color-text-muted)] appearance-none"
                    style={{
                      height: '36px',
                      background: 'var(--color-bg-elevated)',
                      border: '1px solid var(--color-border)',
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '13px',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>

                {postError && (
                  <div
                    className="font-mono text-[11px]"
                    style={{ color: 'var(--color-error, #C24A3A)' }}
                  >
                    INVITE FAILED — {postError}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="font-medium transition-colors hover:text-[var(--color-text-primary)]"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '13px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isEmailValid || busy}
                    className={`inline-flex items-center gap-2 rounded px-3 font-medium transition-colors ${
                      isEmailValid && !busy
                        ? 'border border-[var(--color-needs-human-text)] text-[var(--color-needs-human-text)] hover:bg-[var(--color-needs-human-tint)]'
                        : 'border border-[var(--color-text-muted)] text-[var(--color-text-muted)]'
                    }`}
                    style={{
                      height: '32px',
                      fontFamily: busy ? "'DM Mono', monospace" : "'Outfit', sans-serif",
                      fontSize: busy ? '11px' : '13px',
                    }}
                  >
                    {busy ? 'GENERATING…' : 'Generate link'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <div
                    className="font-mono text-[10px] uppercase tracking-wide"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    GUEST LINK CREATED
                  </div>
                  <div
                    className="mt-1"
                    style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: '18px',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Send this link to your reviewer
                  </div>
                </div>

                <div
                  className="w-full flex items-center gap-2 rounded px-3"
                  style={{
                    height: '40px',
                    background: 'var(--color-bg-elevated)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <Link2
                    className="flex-shrink-0"
                    style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)' }}
                  />
                  <span
                    ref={urlRef}
                    className="min-w-0 flex-1 font-mono truncate"
                    style={{ fontSize: '12px', color: 'var(--color-text-primary)' }}
                  >
                    {result.guest_url}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 rounded px-3 font-medium transition-colors border border-[var(--color-needs-human-text)] text-[var(--color-needs-human-text)] hover:bg-[var(--color-needs-human-tint)]"
                  style={{
                    height: '32px',
                    fontFamily: copied ? "'DM Mono', monospace" : "'Outfit', sans-serif",
                    fontSize: copied ? '11px' : '13px',
                  }}
                >
                  {copied ? (
                    <Check style={{ width: '14px', height: '14px' }} />
                  ) : (
                    <Copy style={{ width: '14px', height: '14px' }} />
                  )}
                  {copied ? 'COPIED' : 'Copy link'}
                </button>

                <div
                  className="font-mono text-[11px]"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  READ-ONLY · EXPIRES{' '}
                  {new Date(result.expires_at)
                    .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                    .toUpperCase()}{' '}
                  · THIS CASE ONLY
                </div>

                <p
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontSize: '12px',
                    color: 'var(--color-text-dim)',
                  }}
                >
                  The link is not emailed. Send it yourself — anyone holding it can view this case
                  until it expires.
                </p>

                <div className="flex items-center justify-end pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="font-medium transition-colors hover:text-[var(--color-text-primary)]"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '13px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
